import React from 'react';
import { Button, DatePicker, Input, Radio, Select, Space, Tag, Tooltip, Typography } from 'antd';
import { DeleteOutlined, LockOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import AddressAutocomplete from '../../components/common/AddressAutocomplete';
import type { AddressSuggestion } from '../../services/addressService';
import type { DeliveryGroupStopDraft, StopExecutionLockState } from './deliveryGroupTypes';
import {
  hydrateStopFromConsignee,
  hydrateStopFromWarehouse,
  hydrateUnloadingStopFromApplication,
  maybeHydrateLoadingFromApplication,
  maybeHydrateUnloadingFromApplication,
  type ApplicationHydrationRef,
  type ConsigneeHydrationRef,
  type WarehouseHydrationRef
} from './executionBindingHelpers';
import { ROUTE_UX } from './routeUxLabels';

const { Text } = Typography;

export type RouteStopCardContext = {
  warehouses?: WarehouseHydrationRef[];
  consignees?: ConsigneeHydrationRef[];
  application?: ApplicationHydrationRef | null;
  groupApplicationId?: number | null;
  applicationConsigneeId?: number | null;
  resolveCityHint?: (address: string) => Promise<string | null>;
  normalizeCity?: (city: string | null | undefined) => string | null;
  inTransit?: boolean;
  allStops?: DeliveryGroupStopDraft[];
};

const CURRENT_EDITABLE_FIELDS = new Set(['contactPerson', 'phone', 'notes']);

function effectiveLockState(stop: Pick<DeliveryGroupStopDraft, 'lockState'>): StopExecutionLockState {
  return stop.lockState ?? 'FUTURE';
}

function isFieldDisabledByLockState(field: string, lockState: StopExecutionLockState): boolean {
  if (lockState === 'FUTURE') return false;
  if (lockState === 'COMPLETED') return true;
  if (lockState === 'CURRENT') return !CURRENT_EDITABLE_FIELDS.has(field);
  return true;
}

function lockHint(lockState: StopExecutionLockState): string | null {
  if (lockState === 'COMPLETED') {
    return 'Точка уже выполнена — изменения могут повлиять на историю исполнения';
  }
  if (lockState === 'CURRENT') {
    return 'Точка в исполнении — доступны только контактные поля';
  }
  return null;
}

type Props = {
  stop: DeliveryGroupStopDraft;
  stopType: 'loading' | 'unloading';
  index: number;
  ctx: RouteStopCardContext;
  onChange: (next: DeliveryGroupStopDraft) => void;
  onRemove: () => void;
  allowRemove?: boolean;
};

function applicationUnloadLabel(app: ApplicationHydrationRef | null | undefined): string {
  if (!app) return ROUTE_UX.application;
  const num = app.internalNumber != null ? String(app.internalNumber).trim() : '';
  return num ? `Заявка №${num}` : `Заявка №${app.id}`;
}

export function RouteGroupStopCard({
  stop,
  stopType,
  index,
  ctx,
  onChange,
  onRemove,
  allowRemove = true
}: Props) {
  const lockState = effectiveLockState(stop);
  const deletable = lockState === 'FUTURE';
  const fieldDisabled = (field: string) => isFieldDisabledByLockState(field, lockState);
  const topologyDisabled = lockState === 'COMPLETED' || lockState === 'CURRENT';
  const isLoading = stopType === 'loading';
  const isPrimaryLoading = isLoading && index === 0;
  const isLegacyConsigneeUnload = !isLoading && stop.pointType === 'consignee';

  const handleAddressChange = async (value: string, suggestion?: AddressSuggestion) => {
    let city = stop.city ?? null;
    if (suggestion?.city) {
      city = ctx.normalizeCity?.(suggestion.city) ?? suggestion.city;
    } else if (ctx.resolveCityHint && value.trim()) {
      city = (await ctx.resolveCityHint(value)) ?? city;
    }
    onChange({ ...stop, address: value, city });
  };

  const fillWarehouse = (warehouseId: number) => {
    const wh = ctx.warehouses?.find((w) => w.id === warehouseId);
    if (!wh) return;
    onChange(hydrateStopFromWarehouse({ ...stop, pointType: 'warehouse', pointId: warehouseId }, wh));
  };

  const fillConsignee = (consigneeId: number) => {
    const c = ctx.consignees?.find((x) => x.id === consigneeId);
    if (!c) return;
    onChange(hydrateStopFromConsignee({ ...stop, pointType: 'consignee', pointId: consigneeId }, c));
  };

  const fillApplicationUnload = () => {
    if (!ctx.application) return;
    onChange(
      hydrateUnloadingStopFromApplication(
        { ...stop, pointType: 'application', pointId: ctx.application.id },
        ctx.application
      )
    );
  };

  React.useEffect(() => {
    if (!ctx.application || isLoading) return;
    if (stop.pointType === 'application' && stop.pointId === ctx.application.id) return;
    if (stop.pointType === 'warehouse') return;
    const hydrated = maybeHydrateUnloadingFromApplication(stop, ctx.application);
    if (hydrated) onChange(hydrated);
  }, [ctx.application, isLoading, onChange, stop]);

  React.useEffect(() => {
    if (!ctx.application || !isLoading || isPrimaryLoading) return;
    const hydrated = maybeHydrateLoadingFromApplication(stop, ctx.application);
    if (hydrated) onChange(hydrated);
  }, [ctx.application, isLoading, isPrimaryLoading, onChange, stop]);

  const unloadRadioValue =
    stop.pointType === 'warehouse'
      ? 'warehouse'
      : stop.pointType === 'consignee'
        ? 'consignee'
        : 'application';

  const showCustomerAddressFields =
    isLoading || isLegacyConsigneeUnload || stop.pointType === 'application';

  return (
    <div
      style={{
        border: '1px solid #f0f0f0',
        borderRadius: 8,
        padding: 12,
        marginBottom: 8,
        background: lockState !== 'FUTURE' ? '#fafafa' : '#fff'
      }}
    >
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <Space>
          <Text strong>
            {isLoading ? 'Точка погрузки' : 'Точка выгрузки'} {index + 1}
          </Text>
          {lockState === 'COMPLETED' ? (
            <Tag color="success">Точка выполнена</Tag>
          ) : lockState === 'CURRENT' ? (
            <Tag color="processing">{ROUTE_UX.currentStopBadge}</Tag>
          ) : null}
        </Space>
        <Space>
          {lockState !== 'FUTURE' && (
            <Tooltip title={lockHint(lockState) || ROUTE_UX.lockedStopHint}>
              <LockOutlined style={{ color: '#faad14' }} />
            </Tooltip>
          )}
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled={!deletable || !allowRemove}
            onClick={onRemove}
          />
        </Space>
      </Space>

      {isLoading ? (
        <>
          {!ctx.groupApplicationId && (
            <Radio.Group
              style={{ marginBottom: 8 }}
              value={stop.pointType || 'warehouse'}
              disabled={topologyDisabled}
              onChange={(e) => onChange({ ...stop, pointType: e.target.value, pointId: null })}
            >
              <Radio value="warehouse">{ROUTE_UX.warehouse}</Radio>
            </Radio.Group>
          )}
          {stop.pointType === 'warehouse' && !ctx.groupApplicationId && (
            <Select
              style={{ width: '100%', marginBottom: 8 }}
              placeholder="Выберите склад"
              showSearch
              disabled={topologyDisabled}
              value={stop.pointId ?? undefined}
              onChange={(val) => fillWarehouse(Number(val))}
              options={ctx.warehouses?.map((w) => ({
                value: w.id,
                label: `${w.name}${w.city ? ` — ${w.city}` : ''}`
              }))}
            />
          )}
        </>
      ) : isLegacyConsigneeUnload ? (
        <>
          <Radio.Group
            style={{ marginBottom: 8 }}
            value={unloadRadioValue}
            disabled={topologyDisabled}
            onChange={(e) => {
              const v = e.target.value;
              onChange({
                ...stop,
                pointType: v === 'warehouse' ? 'warehouse' : 'consignee',
                pointId: null,
                address: null,
                contactPerson: null,
                phone: null,
                plannedAt: null
              });
            }}
          >
            <Radio value="consignee">{ROUTE_UX.consignee}</Radio>
            <Radio value="warehouse">{ROUTE_UX.warehouse}</Radio>
          </Radio.Group>
          {stop.pointType === 'warehouse' ? (
            <Select
              style={{ width: '100%', marginBottom: 8 }}
              placeholder="Выберите склад"
              showSearch
              disabled={topologyDisabled}
              value={stop.pointId ?? undefined}
              onChange={(val) => fillWarehouse(Number(val))}
              options={ctx.warehouses?.map((w) => ({
                value: w.id,
                label: `${w.name}${w.city ? ` — ${w.city}` : ''}`
              }))}
            />
          ) : (
            <Select
              style={{ width: '100%', marginBottom: 8 }}
              placeholder="Выберите получателя"
              showSearch
              disabled={topologyDisabled}
              value={stop.pointId ?? undefined}
              onChange={(val) => fillConsignee(Number(val))}
              options={ctx.consignees?.map((c) => ({ value: c.id, label: c.name }))}
            />
          )}
        </>
      ) : (
        <>
          <Radio.Group
            style={{ marginBottom: 8 }}
            value={unloadRadioValue}
            disabled={topologyDisabled}
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'warehouse') {
                onChange({
                  ...stop,
                  pointType: 'warehouse',
                  pointId: null,
                  address: null,
                  contactPerson: null,
                  phone: null,
                  plannedAt: null
                });
                return;
              }
              fillApplicationUnload();
            }}
          >
            <Radio value="application">{ROUTE_UX.application}</Radio>
            <Radio value="warehouse">{ROUTE_UX.warehouse}</Radio>
          </Radio.Group>
          {stop.pointType === 'warehouse' ? (
            <Select
              style={{ width: '100%', marginBottom: 8 }}
              placeholder="Выберите склад"
              showSearch
              disabled={topologyDisabled}
              value={stop.pointId ?? undefined}
              onChange={(val) => fillWarehouse(Number(val))}
              options={ctx.warehouses?.map((w) => ({
                value: w.id,
                label: `${w.name}${w.city ? ` — ${w.city}` : ''}`
              }))}
            />
          ) : (
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              {applicationUnloadLabel(ctx.application)}
            </Text>
          )}
        </>
      )}

      {showCustomerAddressFields && (
        <Space direction="vertical" style={{ width: '100%' }} size={6}>
          <AddressAutocomplete
            placeholder={ROUTE_UX.address}
            disabled={topologyDisabled}
            style={{ width: '100%' }}
            value={stop.address || ''}
            onChange={handleAddressChange}
          />
          <Space wrap>
            <Input
              placeholder={ROUTE_UX.contact}
              style={{ width: 160 }}
              disabled={fieldDisabled('contactPerson')}
              value={stop.contactPerson || ''}
              onChange={(e) => onChange({ ...stop, contactPerson: e.target.value })}
            />
            <Input
              placeholder={ROUTE_UX.phone}
              style={{ width: 140 }}
              disabled={fieldDisabled('phone')}
              value={stop.phone || ''}
              onChange={(e) => onChange({ ...stop, phone: e.target.value })}
            />
            <DatePicker
              format="DD.MM.YYYY"
              placeholder={ROUTE_UX.date}
              disabled={fieldDisabled('plannedAt')}
              value={stop.plannedAt ? dayjs(stop.plannedAt) : undefined}
              onChange={(d) => onChange({ ...stop, plannedAt: d ? d.toISOString() : null })}
            />
          </Space>
        </Space>
      )}

      {!isLoading && stop.pointType === 'warehouse' && (
        <DatePicker
          format="DD.MM.YYYY"
          placeholder={ROUTE_UX.date}
          disabled={fieldDisabled('plannedAt')}
          style={{ marginTop: 8 }}
          value={stop.plannedAt ? dayjs(stop.plannedAt) : undefined}
          onChange={(d) => onChange({ ...stop, plannedAt: d ? d.toISOString() : null })}
        />
      )}
    </div>
  );
}
